import { Skeleton } from '@tiger/bs-comps';
import React, { useEffect, useRef, useState } from 'react';
import { ModalHandler } from 'components/base/Modal';
import ErrorBox from 'components/ErrorBox';
import classlist from 'utils/classlist';
import { detectScroll, scrollToTop } from './helper';
import { useGetProvinceData } from './hooks';
import { CityType, DistrictType, FormatLetterItemType, SelectTypeEnum } from './type';
import ViewWithNav from './ViewWithNav';

interface ProvincePickerProps extends ModalHandler {
    onCancel: () => void;
    onChange?: (value: string[], closeOption: boolean) => void;
    value: string[];
    okText?: string;
    label?: string;
}

const ProvincePicker: React.FC<ProvincePickerProps> = ({
    label = __('选择地址'),
    okText = __('确定'),
    value,
    close
}) => {
    const { data: dataProvince, isLoading, error, mutate } = useGetProvinceData();
    const valueListRef = useRef<HTMLDivElement>(null);
    const provinceRef = useRef<HTMLDivElement>(null);
    const [{ left, width }, setIndicatorStyle] = useState({
        width: 40,
        left: 0
    });
    const [innerValue, setInnerValue] = useState(value);
    const [provinceList, setProvinceList] = useState<FormatLetterItemType[]>([]);
    const [cityList, setCityList] = useState<CityType[]>([]);
    const [districtList, setDistrictList] = useState<DistrictType[]>([]);
    const [selectType, setSelectType] = useState<SelectTypeEnum>((): SelectTypeEnum => {
        return value.length >= 3 ? 2 : value.length;
    });

    useEffect(() => {
        if (dataProvince) {
            setProvinceList(dataProvince.formatData);

            if (value[0]) {
                const province = dataProvince.data.find(item => item.province === value[0]);

                province && setCityList(province.city);
            }
        }
    }, [dataProvince, value]);

    const handleClick = (name: string, nextType?: SelectTypeEnum) => {
        const newValue = innerValue.slice(0);
        const valueType = typeof nextType === 'number' ? nextType - 1 : 2;

        newValue.splice(valueType, newValue.length - valueType, name);

        setInnerValue(newValue);
        nextType && setSelectType(nextType);

        if (valueType === SelectTypeEnum.DISTRICT) {
            close(newValue);
        }
    };

    useEffect(() => {
        switch (selectType) {
            case SelectTypeEnum.CITY:
                const province = dataProvince?.data?.find(item => item.province === innerValue[0]);

                setCityList(province ? province.city : []);
                break;
            case SelectTypeEnum.DISTRICT:
                const city = cityList.find(item => item.city === innerValue[1]);

                setDistrictList(city ? city.district : []);
                break;
            default:
                break;
        }
    }, [selectType, innerValue, cityList, dataProvince]);

    useEffect(() => {
        const valueListDom = valueListRef.current;

        if (valueListDom) {
            const valueItems = Array.from(valueListDom.children).filter(child =>
                child.classList.contains('value-item')
            );
            const wrapperLeft = valueListDom.getBoundingClientRect().left;
            const activeItem = valueItems[selectType];

            if (activeItem) {
                const { left, width } = activeItem.getBoundingClientRect();

                setIndicatorStyle({
                    width,
                    left: left - wrapperLeft
                });
            }
        }
    }, [selectType, innerValue]);

    useEffect(() => {
        if (provinceRef.current) {
            switch (selectType) {
                case SelectTypeEnum.DISTRICT:
                    if (innerValue[SelectTypeEnum.DISTRICT]) {
                        const index = districtList.findIndex(
                            ({ district }) => district === innerValue[SelectTypeEnum.DISTRICT]
                        );

                        if (index > -1) {
                            detectScroll(provinceRef.current, 'option-item', index);
                        }
                    } else {
                        scrollToTop(provinceRef.current, 0);
                    }

                    break;

                case SelectTypeEnum.CITY:
                    if (innerValue[SelectTypeEnum.CITY]) {
                        const index = cityList.findIndex(({ city }) => city === innerValue[SelectTypeEnum.CITY]);

                        if (index > -1) {
                            detectScroll(provinceRef.current, 'option-item', index);
                        }
                    } else {
                        scrollToTop(provinceRef.current, 0);
                    }

                    break;
                default:
                    break;
            }
        }
    }, [selectType, innerValue, districtList, cityList, provinceList]);

    const renderOptions = () => {
        if (isLoading) {
            return <Skeleton line={5} />;
        }

        if (!dataProvince && error) {
            return <ErrorBox error={error} onClick={() => mutate()} />;
        }

        switch (selectType) {
            case SelectTypeEnum.CITY:
                return cityList.map(({ city }) => (
                    <div
                        key={city}
                        onClick={() => handleClick(city, SelectTypeEnum.DISTRICT)}
                        className={classlist('option-item', {
                            selected: city === innerValue[1]
                        })}>
                        {city}
                    </div>
                ));
            case SelectTypeEnum.DISTRICT:
                return districtList.map(({ district }) => (
                    <div
                        key={district}
                        onClick={() => handleClick(district)}
                        className={classlist('option-item', {
                            selected: district === innerValue[2]
                        })}>
                        {district}
                    </div>
                ));
            default:
                return (
                    provinceList.length > 0 && (
                        <ViewWithNav
                            data={provinceList}
                            value={innerValue[0]}
                            onChange={province => handleClick(province, SelectTypeEnum.CITY)}
                        />
                    )
                );
        }
    };

    return (
        <div className="province-picker">
            <div className="picker-header">
                <span>{label}</span>
                <span
                    onClick={() => {
                        close(innerValue);
                    }}>
                    {okText}
                </span>
            </div>
            <div className="value-list" ref={valueListRef}>
                <span className="value-item" onClick={() => setSelectType(SelectTypeEnum.PROVINCE)}>
                    {innerValue[0] ? innerValue[0] : __('选择省')}
                </span>
                <span className="value-item" onClick={() => setSelectType(SelectTypeEnum.CITY)}>
                    {innerValue[1] ? innerValue[1] : innerValue[0] ? __('选择市') : null}
                </span>
                <span className="value-item" onClick={() => setSelectType(SelectTypeEnum.DISTRICT)}>
                    {innerValue[2] ? innerValue[2] : innerValue[1] ? __('选择区县') : null}
                </span>
                <div className="indicator" style={{ left: `${left}px`, width: `${width}px` }}></div>
            </div>
            <div className="select-view" ref={provinceRef}>
                {renderOptions()}
            </div>
        </div>
    );
};

export default ProvincePicker;
